import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Plus, 
  Eye, 
  EyeOff, 
  Server, 
  Users, 
  RefreshCw, 
  Trash2, 
  AlertTriangle,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const EmailManagement = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    useHash: true
  });
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [dovecotStatus, setDovecotStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Server authentication state
  const [serverConnected, setServerConnected] = useState(false); // Start as false to show modal
  const [serverPassword, setServerPassword] = useState('');
  const [showServerPassword, setShowServerPassword] = useState(false);
  const [connectingToServer, setConnectingToServer] = useState(false);

  useEffect(() => {
    testDovecotConfiguration();
    // Debug: Log the initial state
    console.log('EmailManagement loaded, serverConnected:', false);
  }, []);

  useEffect(() => {
    if (serverConnected) {
      fetchEmailAccounts();
    }
  }, [serverConnected]);

  const verifyServerPassword = async () => {
    console.log('verifyServerPassword called with password length:', serverPassword.length);
    
    if (!serverPassword.trim()) {
      setMessage({ type: 'error', text: 'Please enter the server password' });
      return;
    }

    setConnectingToServer(true);
    setMessage({ type: '', text: '' });

    try {
      console.log('Making request to verify-server-password endpoint...');
      const response = await fetch(`/api/email-management/verify-server-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password: serverPassword })
      });

      const data = await response.json();
      console.log('Server password verification response:', data);

      if (data.success) {
        setServerConnected(true);
        setMessage({ type: 'success', text: 'Connected to server successfully' });
        console.log('Server connected successfully');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to connect to server' });
        console.log('Server connection failed:', data.message);
      }
    } catch (error) {
      console.error('Error verifying server password:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setConnectingToServer(false);
    }
  };

  const testDovecotConfiguration = async () => {
    try {
      const response = await fetch(`/api/email-management/test-dovecot`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setDovecotStatus(data);
    } catch (error) {
      console.error('Error testing Dovecot configuration:', error);
      setDovecotStatus({
        status: 'error',
        message: 'Failed to test configuration'
      });
    }
  };

  const fetchEmailAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await fetch(`/api/email-management/list-emails`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setEmailAccounts(data.accounts || []);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to fetch email accounts' });
        setEmailAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching email accounts:', error);
      setMessage({ type: 'error', text: 'Failed to fetch email accounts' });
      setEmailAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`/api/email-management/create-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `Email account '${formData.email}' created successfully!` 
        });
        setFormData({ email: '', password: '', useHash: true });
        fetchEmailAccounts(); // Refresh the list
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Error creating email account:', error);
      setMessage({ type: 'error', text: 'Failed to create email account' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (email) => {
    try {
      const response = await fetch(`/api/email-management/delete-email/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `Email account '${email}' deleted successfully!` 
        });
        fetchEmailAccounts(); // Refresh the list
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Error deleting email account:', error);
      setMessage({ type: 'error', text: 'Failed to delete email account' });
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const getPasswordTypeColor = (type) => {
    switch (type) {
      case 'hashed': return 'bg-green-100 text-green-800';
      case 'plain': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Server Password Modal */}
      {!serverConnected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Server className="w-8 h-8 text-orange-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Server Authentication Required</h2>
                <p className="text-gray-600">Enter the server password to access email management</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Server Password *
                </label>
                <div className="relative">
                  <input
                    type={showServerPassword ? 'text' : 'password'}
                    value={serverPassword}
                    onChange={(e) => setServerPassword(e.target.value)}
                    placeholder="Enter server password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && verifyServerPassword()}
                    disabled={connectingToServer}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowServerPassword(!showServerPassword)}
                    disabled={connectingToServer}
                  >
                    {showServerPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={verifyServerPassword}
                disabled={connectingToServer || !serverPassword.trim()}
                className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {connectingToServer ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Connecting...
                  </>
                ) : (
                  <>
                    <Server className="w-4 h-4" />
                    Connect to Server
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Only show when server is connected */}
      {serverConnected && (
        <>
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-8 h-8 text-orange-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
                <p className="text-gray-600">Create and manage @swiftfixpro.com email accounts</p>
              </div>
            </div>

            {/* Dovecot Status */}
            {dovecotStatus && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">System Status</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Server:</span>
                    <div className="font-mono text-xs break-all">
                      {dovecotStatus.server || 'localhost'}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Passwd File:</span>
                    <div className="font-mono text-xs break-all">{dovecotStatus.passwdFile}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">File Exists:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      dovecotStatus.fileExists ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {dovecotStatus.fileExists ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Dovecot Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      dovecotStatus.dovecotStatus === 'active' ? 'bg-green-100 text-green-800' : 
                      dovecotStatus.dovecotStatus === 'development' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {dovecotStatus.dovecotStatus}
                    </span>
                  </div>
                </div>
                {dovecotStatus.server && dovecotStatus.server !== 'localhost' && (
                  <div className="mt-3 px-3 py-2 bg-green-100 border border-green-300 rounded text-sm text-green-800">
                    🌐 Connected to production mail server: <strong>{dovecotStatus.server}</strong>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Messages */}
          {message.text && (
            <div className={`rounded-lg p-4 flex items-center gap-2 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Create New Email Account Form */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-6">
                <Plus className="w-6 h-6 text-orange-500" />
                <h2 className="text-xl font-semibold text-gray-900">Create New Email Account</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@swiftfixpro.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter a secure password"
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useHash"
                    name="useHash"
                    checked={formData.useHash}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="useHash" className="ml-2 block text-sm text-gray-700">
                    Use password hashing (recommended)
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Account
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Email Accounts List */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Users className="w-6 h-6 text-orange-500" />
                  <h2 className="text-xl font-semibold text-gray-900">@swiftfixpro.com Accounts</h2>
                </div>
                <button
                  onClick={fetchEmailAccounts}
                  disabled={loadingAccounts}
                  className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loadingAccounts ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </>
                  )}
                </button>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {loadingAccounts ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300 mx-auto mb-4"></div>
                    Loading email accounts...
                  </div>
                ) : emailAccounts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No @swiftfixpro.com email accounts found</p>
                  </div>
                ) : (
                  emailAccounts.map((account, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-gray-500" />
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              {account.email}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div>UID: {account.uid} | GID: {account.gid}</div>
                            <div className="font-mono break-all">Home: {account.home}</div>
                            {account.passwordType && (
                              <div className="flex items-center gap-2">
                                <span>Password:</span>
                                <span className={`px-2 py-1 rounded text-xs ${getPasswordTypeColor(account.passwordType)}`}>
                                  {account.passwordType}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => setShowDeleteConfirm(account.email)}
                          className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete Account"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                    <p className="text-gray-600">This action cannot be undone</p>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-gray-700 mb-2">Are you sure you want to delete this email account?</p>
                  <p className="font-mono text-sm bg-gray-100 p-2 rounded border">
                    {showDeleteConfirm}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteEmail(showDeleteConfirm)}
                    className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailManagement;